/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { AnyAction, Reducer } from 'redux';
import { takeLatest, put, select } from 'redux-saga/effects';
import { NETWORKS, INITIAL_RANDOM_NODE, RPC_NODES } from '@constants';
import { ILooseObject } from '@interface/loose-object';
import { ISagaEvent } from '@interface/saga-event';
import { logout, changeBeacon } from '@lib/beacon';
import { getUserPreferences, getUserProfile, setUserProfile } from '@lib/versum';
import { buildNodesFromNetwork } from '@utils/wallet';
import { actions as globalActions } from '../global';
import { actions as modalsActions } from '../modals';
import { actions as settingsActions } from '../settings';

interface IAccount {
  accountIdentifier: string;
  senderId: string;
  origin: Record<string, string>;
  address: string;
  publicKey: string;
  network: Record<string, string>;
  scopes: string[];
  connectedAt: number;
}

export interface IAuth {
  account: IAccount | null;
  user: {
    id: string;
    name: string;
    profile_image_uri: string;
    profile_banner_uri: string;
    name_is_custom: boolean;
    name_is_generative: boolean;
    name_score: number;
    screen_name: string;
    profile_bio: string;
    verified: boolean;
    verified_on_chain: boolean;
  } | null;
}

export const initialState: IAuth = {
  account: null,
  user: null,
};

export enum actions {
  AUTH_LOGIN = 'AUTH_LOGIN',
  AUTH_LOGOUT = 'AUTH_LOGOUT',
  AUTH_SET_LOGGED_USER = 'AUTH_SET_LOGGED_USER',
  AUTH_UPDATE_PROFILE = 'AUTH_UPDATE_PROFILE',
}

export const reducer: Reducer<IAuth, AnyAction> = (state = initialState, action) => {
  switch (action.type) {
    case actions.AUTH_LOGIN:
      return { ...state, account: { ...action.payload } };
    case actions.AUTH_LOGOUT:
      return { ...state, account: null, user: null };
    case actions.AUTH_SET_LOGGED_USER:
      return { ...state, user: { ...action.payload } };
    default:
      return state;
  }
};

function* getPreferences() {
  const {
    statusCode,
    id,
    name,
    name_is_custom,
    name_is_generative,
    name_score,
    verified,
    verified_on_chain,
    profile_image_uri,
    profile_banner_uri,
    profile_bio,
    screen_name,
  } = yield getUserProfile();

  // if next-auth token expired, force logout
  if (statusCode === 401) {
    yield logout();
    yield put({ type: actions.AUTH_LOGOUT });
    return;
  }

  const user = {
    id,
    name,
    profile_image_uri,
    profile_banner_uri,
    name_is_custom,
    name_is_generative,
    name_score,
    screen_name,
    profile_bio,
    verified,
    verified_on_chain,
  };

  // get preferences
  const { preferences }: ILooseObject = yield getUserPreferences(user.id);

  // checks the user saved network and node and compares it against
  // the available data on @constants. If value on user preferences doesn't exist on @constants
  // it reassigns a new network and node and updates the preferences
  const sanitiseNetworkAndNode = (userNetwork: string, userNode: string) => {
    let nextNetwork;
    let nextNode;

    // look up network on constants
    // if not found add the default one.
    const foundNetwork = NETWORKS.find((n: any) => n.value === userNetwork);
    if (!foundNetwork) {
      nextNetwork = (process.env.LOGGEDOUT_TEZOS_NETWORK as string) || 'mainnet';
      nextNode = INITIAL_RANDOM_NODE.value;
    } else {
      nextNetwork = foundNetwork.value;
    }

    // do the same for the nodes
    const nodes = buildNodesFromNetwork(nextNetwork, RPC_NODES);
    const foundNode = nodes.find((n: any) => n.value === userNode);
    if (!foundNode) {
      nextNode = INITIAL_RANDOM_NODE.value;
    } else {
      nextNode = foundNode.value;
    }

    return {
      nextNetwork,
      nextNode,
    };
  };

  // setup user
  yield put({ type: actions.AUTH_SET_LOGGED_USER, payload: user });

  // setup preferences
  if (preferences && Object.keys(preferences).length > 0) {
    yield put({ type: settingsActions.SETTINGS_SET_PREFERENCES, payload: preferences });

    const { nextNetwork, nextNode } = sanitiseNetworkAndNode(preferences.general.network, preferences.general.node);
    // yield put({ type: settingsActions.SETTINGS_CHANGE_NETWORK, payload: nextNetwork });
    // yield put({ type: settingsActions.SETTINGS_CHANGE_NODE, payload: nextNode });
    changeBeacon(nextNetwork, nextNode);

    // if user preferences has different number show what's new panel
    if (preferences.changelog !== process.env.CHANGELOG && preferences.general.changelog) {
      yield put({ type: modalsActions.MODALS_SET_MODAL, payload: { type: 'whats-new' } });
    }

    // update crypto to fiat convertion
    yield put({ type: globalActions.GLOBAL_GET_TEZOS, payload: preferences.general.currency });
  } else {
    // first time users
    yield put({ type: modalsActions.MODALS_SET_MODAL, payload: { type: 'user-onboarding' } });
    yield put({ type: settingsActions.SETTINGS_SAVE_PREFERENCE_FIRST_TIME_USER });
  }

  // setup boards
  yield put({ type: globalActions.GLOBAL_GET_BOARDS, payload: user });

  // setup collaborations
  yield put({ type: globalActions.GLOBAL_GET_COLLABORATIONS, payload: user });

  // setup user notifications
  yield put({ type: globalActions.GLOBAL_GET_SYSTEM_NOTIFICATIONS, payload: user });

  // get currencies
  yield put({ type: globalActions.GLOBAL_GET_CURRENCIES });

  // application is ready
  yield put({ type: globalActions.GLOBAL_READY, payload: true });
}

function* updateProfile(event: ISagaEvent) {
  const { user } = yield select((state) => state.auth);

  const { name, bio, avatar, cover } = event.payload;

  const nextUser = {
    ...user,
    screen_name: name,
    profile_bio: bio,
    ...(avatar?.blob && { profile_image_uri: avatar?.blob }),
    ...(cover?.blob && { profile_banner_uri: cover?.blob }),
  };

  // update redux user
  yield put({ type: actions.AUTH_SET_LOGGED_USER, payload: nextUser });

  // call api with new user profile
  yield setUserProfile({
    ...nextUser,
    ...(avatar?.ipfs && { profile_image_uri: avatar?.ipfs }),
    ...(cover?.ipfs && { profile_banner_uri: cover?.ipfs }),
  });
}

export function* saga() {
  yield takeLatest(actions.AUTH_LOGIN, getPreferences);
  yield takeLatest(actions.AUTH_UPDATE_PROFILE, updateProfile);
}
